import type {
  AckArgsWithoutAck,
  AckEvents,
  AckReturnData,
  ClientToServerEvents,
  NonAckArgs,
  NonAckEvents,
  SocketAcknowledgement,
} from '@game/shared-types';
import { parser } from '@mjfwebb/socket.io-cbor-x-parser';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';

type SendToServer = <Event extends NonAckEvents<ClientToServerEvents>>(event: Event, ...args: NonAckArgs<ClientToServerEvents, Event>) => void;

type SendToServerPromise = <Event extends AckEvents<ClientToServerEvents>>(
  event: Event,
  ...args: AckArgsWithoutAck<ClientToServerEvents, Event>
) => Promise<AckReturnData<ClientToServerEvents, Event>>;

interface SocketManagerInterface {
  socket: Socket | null;
  sendToServer: SendToServer;
  sendToServerPromise: SendToServerPromise;
  connect: (authToken: string, serverUrl: string, eventHandlers: (socket: Socket) => void) => void;
  disconnect: () => void;
  isConnected: () => boolean;
  onConnect: (callback: () => void) => void;
  onDisconnect: (callback: (reason: string) => void) => void;
  onConnectError: (callback: (error: Error) => void) => void;
  onReconnect: (callback: () => void) => void;
  onReconnectAttempt: (callback: (attemptNumber: number) => void) => void;
}

class SocketManager implements SocketManagerInterface {
  socket: Socket | null = null;
  private connectCallbacks: Set<() => void> = new Set();
  private disconnectCallbacks: Set<(reason: string) => void> = new Set();
  private connectErrorCallbacks: Set<(error: Error) => void> = new Set();
  private reconnectCallbacks: Set<() => void> = new Set();
  private reconnectAttemptCallbacks: Set<(attemptNumber: number) => void> = new Set();

  connect(authToken: string, serverUrl: string, eventHandlers: (socket: Socket) => void): void {
    if (this.socket?.connected) {
      return;
    }

    this.socket = io(serverUrl, {
      parser,
      auth: { token: authToken },
      autoConnect: false,
    });

    // Setup event listeners
    this.socket.on('connect', () => {
      for (const cb of this.connectCallbacks) {
        cb();
      }
    });

    this.socket.on('disconnect', (reason: string) => {
      for (const cb of this.disconnectCallbacks) {
        cb(reason);
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      for (const cb of this.connectErrorCallbacks) {
        cb(error);
      }
    });

    this.socket.io.on('reconnect', () => {
      for (const cb of this.reconnectCallbacks) {
        cb();
      }
    });

    this.socket.io.on('reconnect_attempt', (attemptNumber: number) => {
      for (const cb of this.reconnectAttemptCallbacks) {
        cb(attemptNumber);
      }
    });

    eventHandlers(this.socket);

    this.socket.open();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  sendToServer: SendToServer = (event, ...args) => {
    if (this.socket) {
      this.socket.emit(event, ...args);
    }
  };

  sendToServerPromise: SendToServerPromise = (event, ...args) =>
    new Promise((resolve, reject) => {
      if (!this.socket) {
        reject(new Error('No connection.'));
        return;
      }

      // eslint-disable-next-line func-style
      const ack: SocketAcknowledgement<AckReturnData<ClientToServerEvents, typeof event>> = (response?): void => {
        if (!response) {
          // Void response, resolve immediately
          resolve(undefined as AckReturnData<ClientToServerEvents, typeof event>);
          return;
        }

        if (response.error) {
          reject(new Error(response.error.message));
        } else if ('data' in response) {
          resolve(response.data);
        }
      };

      this.socket.emit(event, ...args, ack);
    });

  // Event subscription methods
  onConnect(callback: () => void): void {
    this.connectCallbacks.add(callback);
  }

  onDisconnect(callback: (reason: string) => void): void {
    this.disconnectCallbacks.add(callback);
  }

  onConnectError(callback: (error: Error) => void): void {
    this.connectErrorCallbacks.add(callback);
  }

  onReconnect(callback: () => void): void {
    this.reconnectCallbacks.add(callback);
  }

  onReconnectAttempt(callback: (attemptNumber: number) => void): void {
    this.reconnectAttemptCallbacks.add(callback);
  }

  // Cleanup methods
  offConnect(callback: () => void): void {
    this.connectCallbacks.delete(callback);
  }

  offDisconnect(callback: (reason: string) => void): void {
    this.disconnectCallbacks.delete(callback);
  }

  offConnectError(callback: (error: Error) => void): void {
    this.connectErrorCallbacks.delete(callback);
  }

  offReconnect(callback: () => void): void {
    this.reconnectCallbacks.delete(callback);
  }

  offReconnectAttempt(callback: (attemptNumber: number) => void): void {
    this.reconnectAttemptCallbacks.delete(callback);
  }
}

export const socketManager = new SocketManager();
